import {
  IFacility,
  ISeasonalHoursFacility,
  ISpecialHoursFacility,
  IWeekdayHoursFacility,
} from "../../types/facility";

export type FacilityCourtType = "INDOOR" | "OUTDOOR" | "REHAB";

export const COURT_TYPES: FacilityCourtType[] = ["INDOOR", "OUTDOOR", "REHAB"];

export const COURT_LABELS: Record<FacilityCourtType, string> = {
  INDOOR: "Inne",
  OUTDOOR: "Ute",
  REHAB: "Rehab",
};

export type FacilityByCourt = Partial<Record<FacilityCourtType, IFacility>>;

/**
 * A single row in the UI, standing for the same setting across one or more
 * court types. `courtTypes` is what exists; `missing` is what doesn't — which
 * is usually the more interesting half, since a day configured for two courts
 * out of three is nearly always an oversight.
 */
export interface IMergedRow<T> {
  key: string;
  courtTypes: FacilityCourtType[];
  missing: FacilityCourtType[];
  /** The record per court type, so an edit can be applied to each. */
  records: Partial<Record<FacilityCourtType, T>>;
  /** Any one of the records — they agree on everything the row displays. */
  sample: T;
}

/**
 * Records only merge when they agree on everything shown in the row. Two
 * courts sharing a date but not a closing time stay separate, because
 * collapsing them would show a time that isn't true for one of them.
 */
function groupBy<T>(
  byCourt: FacilityByCourt,
  pick: (f: IFacility) => T[] | undefined,
  signature: (record: T) => string,
): IMergedRow<T>[] {
  const groups = new Map<string, IMergedRow<T>>();

  COURT_TYPES.forEach((courtType) => {
    const facility = byCourt[courtType];
    if (!facility) return;

    (pick(facility) ?? []).forEach((record) => {
      const key = signature(record);
      let row = groups.get(key);
      if (!row) {
        row = {
          key,
          courtTypes: [],
          missing: [],
          records: {},
          sample: record,
        };
        groups.set(key, row);
      }
      row.courtTypes.push(courtType);
      row.records[courtType] = record;
    });
  });

  const rows = Array.from(groups.values());
  rows.forEach((row) => {
    row.missing = COURT_TYPES.filter(
      // Only count a court type as missing if it was actually loaded.
      (c) => byCourt[c] && !row.courtTypes.includes(c),
    );
  });
  return rows;
}

const dateOnly = (value?: string) => (value ?? "").split("T")[0];

export function mergeSpecials(
  byCourt: FacilityByCourt,
): IMergedRow<ISpecialHoursFacility>[] {
  return groupBy<ISpecialHoursFacility>(
    byCourt,
    (f) => f.special_days,
    (d) =>
      [
        dateOnly(d.date),
        d.alias,
        d.closed ? "closed" : `${d.opening_hours}-${d.closing_hours}`,
      ].join("|"),
  ).sort((a, b) =>
    dateOnly(a.sample.date).localeCompare(dateOnly(b.sample.date)),
  );
}

export function mergeSeasonals(
  byCourt: FacilityByCourt,
): IMergedRow<ISeasonalHoursFacility>[] {
  return groupBy<ISeasonalHoursFacility>(
    byCourt,
    (f) => f.seasonal_hours,
    (s) =>
      [
        dateOnly(s.start_date),
        dateOnly(s.end_date),
        s.alias,
        s.closed
          ? "closed"
          : (s.weekday_hours ?? [])
              .map(
                (w) => `${w.weekday}:${w.opening_hours}-${w.closing_hours}`,
              )
              .sort()
              .join(","),
      ].join("|"),
  ).sort((a, b) =>
    dateOnly(a.sample.start_date).localeCompare(dateOnly(b.sample.start_date)),
  );
}

export function mergeDefaults(
  byCourt: FacilityByCourt,
): IMergedRow<IWeekdayHoursFacility>[] {
  return groupBy<IWeekdayHoursFacility>(
    byCourt,
    (f) => f.default_hours,
    (w) =>
      [w.weekday, w.opening_hours, w.closing_hours].join("|"),
  ).sort(
    (a, b) =>
      WEEKDAY_ORDER.indexOf(a.sample.weekday?.toLowerCase()) -
      WEEKDAY_ORDER.indexOf(b.sample.weekday?.toLowerCase()),
  );
}

const WEEKDAY_ORDER = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const WEEKDAY_SHORT: Record<string, string> = {
  monday: "mån",
  tuesday: "tis",
  wednesday: "ons",
  thursday: "tor",
  friday: "fre",
  saturday: "lör",
  sunday: "sön",
};

/** "08:00:00" -> "08:00". Always HH:MM, so the columns line up. */
export function shortTime(value?: string): string {
  if (!value) return "";
  const [hh, mm] = value.split(":");
  return `${hh}:${mm ?? "00"}`;
}

/**
 * Collapse seven weekday rows into something readable:
 * "mån–tor 08:00–21:00 · fre–sön 08:00–18:30". Consecutive days sharing the
 * same hours become a range; a season with identical hours all week reduces to
 * one span.
 */
export function summariseWeekdayHours(
  weekdayHours?: IWeekdayHoursFacility[],
): string {
  if (!weekdayHours?.length) return "";

  const ordered = WEEKDAY_ORDER.map((day) =>
    weekdayHours.find((w) => w.weekday?.toLowerCase() === day),
  );

  const spans: { from: string; to: string; hours: string }[] = [];
  ordered.forEach((entry, index) => {
    if (!entry) return;
    const hours = `${shortTime(entry.opening_hours)}–${shortTime(
      entry.closing_hours,
    )}`;
    const day = WEEKDAY_ORDER[index];
    const previous = spans[spans.length - 1];
    // Extend the running span only if the previous weekday was present and
    // shares these hours, so a gap in the week doesn't get bridged.
    if (previous && previous.hours === hours && ordered[index - 1]) {
      previous.to = day;
    } else {
      spans.push({ from: day, to: day, hours });
    }
  });

  return spans
    .map(({ from, to, hours }) =>
      from === to
        ? `${WEEKDAY_SHORT[from]} ${hours}`
        : `${WEEKDAY_SHORT[from]}–${WEEKDAY_SHORT[to]} ${hours}`,
    )
    .join(" · ");
}

/** "2026-04-30" -> "30 apr" */
const MONTHS = [
  "jan",
  "feb",
  "mar",
  "apr",
  "maj",
  "jun",
  "jul",
  "aug",
  "sep",
  "okt",
  "nov",
  "dec",
];

export function shortDate(value?: string): string {
  const [year, month, day] = dateOnly(value).split("-");
  if (!year || !month || !day) return dateOnly(value);
  return `${Number(day)} ${MONTHS[Number(month) - 1] ?? month}`;
}

export function shortRange(from?: string, to?: string): string {
  return `${shortDate(from)} – ${shortDate(to)}`;
}
